import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import { getNames } from "country-list";
import { AddEmployeeReq, PictureObject } from "../types/Employee";

import {
  ButtonPrimary,
  ButtonSecondary,
  HStack,
  VStack,
  Error,
  ButtonPrimary1,
  Center,
} from "../components/utils";

import { Input, Label, TextArea } from "../components/shared/InputField";
import { Select } from "../components/shared/Select";
import {
  useAddEmployee,
  useEmployeeDepartment,
  useEmployeementStatusDropdown,
  useEmployeeStatus,
  useSubsidiaryCompanyNames,
} from "../queries";

import { formatWithTimeZone } from "../utils/date";
import { useError } from "../store/errorStore";
import convertToBase64 from "../utils/convertToBase64";
import { initialPictureValues } from "../pages/employeeDetails/profile";
import EsopModellingGrades from "../constants/EsopModellingGrades";

type AddOrEditEmployee = {
  mode?: "Edit" | "Add";
  data: any[];
  onClose: () => void;
};

const AddEmployee = (props: AddOrEditEmployee) => {
  const [profilePicture, setProfilePicture] =
    useState<PictureObject>(initialPictureValues);
  const [imageURL, setImageURL] = useState("");
  const [fileType, setFileType] = useState("");
  const [fileName, setFileName] = useState("");
  const errorMessage = useError();
  const { data, mode, onClose } = props;
  const { data: _employementStatuses } = useEmployeeStatus();
  const { data: _statusDropdown } = useEmployeementStatusDropdown();
  const statusDropdown = _statusDropdown || [];
  const employeeStatuses = _employementStatuses || [];
  const { data: _employeeDepartments } = useEmployeeDepartment();
  const employeeDepartments = _employeeDepartments || [];
  const validationSchema = Yup.object().shape({
    employeeName: Yup.string().required("Employee name is required"),
    employeeDesignation: Yup.string(),
    contactNumber: Yup.string()
      .matches(/^[0-9]+$/, "Must be only digits")
      .min(10, "Must be exactly 10 digits")
      .max(10, "Must be exactly 10 digits"),
    department: Yup.string().required("Department is required"),
    email: Yup.string().email().required("Valid Email is required"),
    residentialStatus: Yup.string().required("Coountry is required"),
    nationality: Yup.string().required("Nationality is required"),
    employeeIdentification: Yup.string().required("ID is required"),
    dateOfJoin: Yup.string().required("Joining Date is required"),
    employmentStatus: Yup.string().required(" Employment Status is required"),
    lastDay: Yup.string().when("employmentStatus", {
      is: "Resigned",
      then: Yup.string().required("Date Required"),
    }),
  });

  const intialValues: AddEmployeeReq = {
    employeeName: "",
    employeeDesignation: "",
    address: "",
    department: "",
    email: "",
    manager: "",
    residentialStatus: "India",
    nationality: "India",
    legalName: "",
    employeeIdentification: "",
    dateOfJoin: "",
    grade: "",
    employmentStatus: "",
    company: "",
    notes: "",
    employeeImagePath: "",
    employmentType: "EMPLOYEE",
    pan: "",
    profilePicture: initialPictureValues,
    id: null,
    subsidiaryCompanyName: "",
  };

  const { mutate: addEmployee, isLoading } = useAddEmployee();
  const { data: subsidiaryCompaniesDetail } = useSubsidiaryCompanyNames();
  const subsidiaryCompanyNamesList = (subsidiaryCompaniesDetail || []).map(
    (company) => company.companyName
  );
  const handleSubmit = (values: AddEmployeeReq) => {
    const companyName = values.subsidiaryCompanyName;
    const esopSubsidiaryCompany = (subsidiaryCompaniesDetail || []).filter(
      (item) => item.companyName === companyName
    )[0];
    const employeeDto = { ...values };
    employeeDto.esopSubsidiaryCompany = esopSubsidiaryCompany;
    if (
      employeeDto.employmentStatus === "Resigned" &&
      employeeDto.lastDay !== undefined
    ) {
      employeeDto.lastDay = formatWithTimeZone(
        employeeDto.lastDay || "2001-1-1"
      );
    }
    employeeDto.dateOfJoin = formatWithTimeZone(employeeDto.dateOfJoin);
    employeeDto.legalName = employeeDto.employeeName;
    employeeDto.profilePicture = profilePicture;
    addEmployee(employeeDto, {
      onSuccess: () => {
        toast("Employee Added Successfully!", {
          type: "success",
          autoClose: 2000,
        });
        onClose();
      },
      onError: (err: any) => {
        errorMessage.setMessage(err.response.data.reason);
        toast(err.response.data.reason, { type: "error", autoClose: 2000 });
      },
    });
  };

  async function handleEditImage(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e?.target?.files?.[0]) return;
    const file = e.target.files[0];
    const base64 = await convertToBase64(file);
    setImageURL(URL.createObjectURL(e.target.files[0]));
    if (
      typeof base64 === "string" &&
      typeof file.type === "string" &&
      typeof file.name === "string"
    ) {
      setProfilePicture({
        picture: base64,
        fileType: file.type,
        fileName: file.name,
      });
    }
  }
  return (
    <>
      <Box className="px-10 text-lg font-medium border-b py-7">
        <h6 className="flex justify-between">
          {mode === "Edit" ? "Edit Option Holder" : "Add New Option Holder"}{" "}
          <span onClick={() => onClose()} className="cursor-pointer">
            X
          </span>{" "}
        </h6>
      </Box>
      <VStack className="w-full px-10 py-7 gap-9">
        <Formik
          initialValues={intialValues}
          onSubmit={(values) => {
            handleSubmit(values);
          }}
          validationSchema={validationSchema}
        >
          {(formik) => (
            <>
              <Form>
                <VStack className="w-full gap-9">
                  <HStack className="gap-8 ">
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Option Holder Name
                      </Label>
                      <Input
                        type="text"
                        {...formik.getFieldProps("employeeName")}
                      />
                      {formik.touched.employeeName &&
                        formik.errors.employeeName && (
                          <Error text={formik.errors.employeeName} />
                        )}
                    </div>
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Option Holder Type
                      </Label>
                      <Select
                        options={["EMPLOYEE", "CONSULTANT"]}
                        {...formik.getFieldProps("employmentType")}
                      />
                      {formik.touched.employmentType &&
                        formik.errors.employmentType && (
                          <Error text={formik.errors.employmentType} />
                        )}
                    </div>
                  </HStack>
                  <HStack className="gap-8 ">
                    <div className="flex-1">
                      <Label className="text-sm font-normal">Employee ID</Label>
                      <Input
                        type="text"
                        {...formik.getFieldProps("employeeIdentification")}
                      />
                      {formik.touched.employeeIdentification &&
                        formik.errors.employeeIdentification && (
                          <Error text={formik.errors.employeeIdentification} />
                        )}
                    </div>
                    <div className="flex-1">
                      <Label className="text-sm font-normal">Designation</Label>
                      <Input
                        type="text"
                        {...formik.getFieldProps("employeeDesignation")}
                      />
                      {formik.touched.employeeDesignation &&
                        formik.errors.employeeDesignation && (
                          <Error text={formik.errors.employeeDesignation} />
                        )}
                    </div>
                  </HStack>
                  <HStack className="gap-8 ">
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Organization
                      </Label>
                      <Select
                        className={`${
                          subsidiaryCompanyNamesList.length < 1
                            ? "cursor-not-allowed"
                            : ""
                        }`}
                        disabled={subsidiaryCompanyNamesList.length < 1}
                        options={subsidiaryCompanyNamesList}
                        {...formik.getFieldProps("subsidiaryCompanyName")}
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Date of Joining
                      </Label>
                      <Input
                        type="date"
                        {...formik.getFieldProps("dateOfJoin")}
                      />
                      {formik.touched.dateOfJoin &&
                        formik.errors.dateOfJoin && (
                          <Error text={formik.errors.dateOfJoin} />
                        )}
                    </div>
                  </HStack>
                  <HStack className="gap-8 ">
                    <div className="flex-1">
                      <Label className="text-sm font-normal">Email ID</Label>
                      <Input type="email" {...formik.getFieldProps("email")} />
                      {formik.touched.email && formik.errors.email && (
                        <Error text={formik.errors.email} />
                      )}
                    </div>
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Contact Number
                      </Label>
                      <Input
                        type="number"
                        {...formik.getFieldProps("contactNumber")}
                      />
                      {formik.touched.contactNumber &&
                        formik.errors.contactNumber && (
                          <Error text={formik.errors.contactNumber} />
                        )}
                    </div>
                  </HStack>
                  <HStack className="gap-8 ">
                    <div className="flex-1">
                      <Label className="text-sm font-normal">Department</Label>
                      <Input
                        {...formik.getFieldProps("department")}
                        type="text"
                      />
                      {formik.touched.department &&
                        formik.errors.department && (
                          <Error text={formik.errors.department} />
                        )}
                    </div>
                    <div className="flex-1">
                      <Label className="text-sm font-normal">Grade</Label>
                      <Select
                        options={EsopModellingGrades}
                        {...formik.getFieldProps("grade")}
                      />
                    </div>
                  </HStack>
                  <HStack className="gap-8 ">
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Country of Residence
                      </Label>
                      <Select
                        options={getNames().sort()}
                        {...formik.getFieldProps("residentialStatus")}
                      />
                      {formik.touched.residentialStatus &&
                        formik.errors.residentialStatus && (
                          <Error text={formik.errors.residentialStatus} />
                        )}
                    </div>
                    <div className="flex-1">
                      <Label className="text-sm font-normal">Nationality</Label>
                      <Select
                        options={getNames().sort()}
                        {...formik.getFieldProps("nationality")}
                      />
                      {formik.touched.nationality &&
                        formik.errors.nationality && (
                          <Error text={formik.errors.nationality} />
                        )}
                    </div>
                  </HStack>
                  <HStack className="gap-8 ">
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Employment Status
                      </Label>
                      <Select
                        options={statusDropdown}
                        {...formik.getFieldProps("employmentStatus")}
                      />
                      {formik.touched.employmentStatus &&
                        formik.errors.employmentStatus && (
                          <Error text={formik.errors.employmentStatus} />
                        )}
                    </div>
                    <div className="flex-1">
                      <Label className="text-sm font-normal">
                        Date of Resignation
                      </Label>
                      <Input
                        type="date"
                        disabled={
                          !(formik.values.employmentStatus !== "Employed") ||
                          !(formik.values.employmentStatus !== "")
                        }
                        {...formik.getFieldProps("lastDay")}
                      />
                      {formik.touched.lastDay && formik.errors.lastDay && (
                        <Error text={formik.errors.lastDay} />
                      )}
                    </div>
                  </HStack>
                  <HStack className="gap-8 ">
                    <div className="flex-1">
                      <Label className="text-sm font-normal">Address</Label>
                      <TextArea {...formik.getFieldProps("address")} />
                    </div>
                    <div className="flex flex-row justify-between flex-1">
                      <Box>
                        <Label className="text-sm font-normal">
                          Profile Photo
                        </Label>
                      </Box>
                      <Box className="">
                        {imageURL === "" ? (
                          <label className="px-2 py-2 rounded-lg cursor-pointer bg-slate-100">
                            <Input
                              type="file"
                              className="hidden"
                              accept="image/png, image/jpeg,image/jpg"
                              onChange={handleEditImage}
                            />
                            Upload Image
                          </label>
                        ) : (
                          <div className="relative flex flex-col justify-between">
                            <Label>
                              <input
                                type="file"
                                accept="image/png, image/jpg, image/jpeg"
                                className="hidden"
                                onChange={handleEditImage}
                              />
                              <Icon
                                icon={"eva:edit-outline"}
                                className="absolute z-10 w-6 h-6 text-gray-400 bg-gray-100 rounded-full cursor-pointer -right-2 hover:text-black"
                              />
                            </Label>
                            <img
                              className="right-0 block w-32 h-32 space-y-3 bg-gray-400 border-2 rounded-lg"
                              src={imageURL}
                              alt="avatar"
                            />
                            <Label>
                              <Icon
                                icon={"eva:close-fill"}
                                onClick={() => {
                                  setProfilePicture(initialPictureValues);
                                  setImageURL("");
                                }}
                                className="absolute z-10 w-6 h-6 text-gray-400 bg-gray-100 rounded-full cursor-pointer -right-2 -bottom-2 hover:text-black"
                              />
                            </Label>
                          </div>
                        )}
                      </Box>
                    </div>
                  </HStack>
                  <HStack className="gap-8 ">
                    <div className="flex-1">
                      <Label className="text-sm font-normal">Notes</Label>
                      <TextArea {...formik.getFieldProps("notes")} />
                    </div>
                    <div className="flex-1"></div>
                  </HStack>
                  <HStack className="justify-between">
                    <ButtonPrimary1
                      type="reset"
                      className="text-red-500"
                      onClick={onClose}
                    >
                      {" "}
                      Back
                    </ButtonPrimary1>
                    <ButtonPrimary type="submit">
                      {!isLoading ? (
                        "Create Option Holder"
                      ) : (
                        <Icon
                          className="animate-spin"
                          icon="lucide:loader-2"
                          width={36}
                        />
                      )}
                    </ButtonPrimary>
                  </HStack>
                </VStack>
              </Form>
            </>
          )}
        </Formik>
      </VStack>
    </>
  );
};

export default AddEmployee;
